/**
 * ColorCoding.js
 * 
 * 1. Have admin press 'Continue' this triggers
 *  -   Assignment of players randomly to teams of number of impostors - 1
 *  -   You may update the ColorGrids -> grid with members : [] having the userIDs of the users in that team
 *  -   Make sure if the response is true of assignment, the continue button never comes again.
 * 2. User Fetches the grid
 * 2. Check if `opened` field is true, 
 *  -   if not,
 *      -   Start a timer for 45 secs
 *      -   update the doc to opened true and the finishedAt to now + 45 secs
 *  -   if yes,
 *      -   check if the timer is past or not
 * 
 * 3. If opened and timer is past, show a blank 6x6 grid, let the users tap on a box and decide which color it was
 *  -   have the colors one after the other, if pressed once = red, another tap = yellow and so on...
 * 
 * 4. Let user press submit, once it is submitted -> update the doc with updated grid
 * 5. Keep a listener, checking if everyone has submitted their answerGrid, if yes, display the result on everyone's screens
 * 
 */

import React, { useEffect, useState } from "react";
import { assignMeAGrid, submitAnswerGrid } from "../helpers/colorGridHelpers";
import ColorGridDisplay from "../components/ColorGridDisplay";
import ColorGridAnswer from "../components/ColorgridAnswer";
import { handleContinueClick, listenForAllReady } from "../helpers/stageProgressHelpers";

const COLOR_CYCLE = ["Red", "Yellow", "Blue", "Green"];

const TimerDisplay = ({ seconds }) => (
    <h2 className="text-2xl sm:text-3xl font-semibold text-zinc-200 mb-6">
        {Math.floor(seconds / 60)
            .toString()
            .padStart(2, "0")}
        :
        {(seconds % 60).toString().padStart(2, "0")}
    </h2>
);

export default function ColorCoding({ room, user, players, stage, setStage }) {
    const [selectedGrid, setSelectedGrid] = useState(null);
    const [answers, setAnswers] = useState(Array(36).fill(null));
    const [score, setScore] = useState(null);
    const [timer, setTimer] = useState(45);
    const [finishAt, setFinishAt] = useState(null);
    const [localStage, setLocalStage] = useState("waiting"); // waiting → showing → answering → result
    const [hasClickedContinue, setHasClickedContinue] = useState(false);

    /** Continue → Assign me a grid */
    const handleContinue = async () => {
        const assignment = await assignMeAGrid(user, room.id);
        if (!assignment) {
            setLocalStage('result');
            return alert("No grids left!");
        }
        setSelectedGrid(assignment.grid);
        setFinishAt(assignment.finishesAt);
        setLocalStage("showing");
    };

    /** 45-second countdown when showing grid */
    useEffect(() => {
        if (localStage !== "showing" || !finishAt) return;
        const interval = setInterval(() => {
            const remaining = Math.max(0, Math.floor((finishAt - Date.now()) / 1000));
            setTimer(remaining);
            if (remaining <= 0) {
                clearInterval(interval);
                setLocalStage("answering");
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [localStage, finishAt]);

    /** Handle color cycling on click */
    const handleBoxClick = (index) => {
        if (localStage !== "answering") return;
        setAnswers((prev) => {
            const newGrid = [...prev];
            const current = prev[index];
            const nextColor =
                COLOR_CYCLE[(COLOR_CYCLE.indexOf(current) + 1) % COLOR_CYCLE.length];
            newGrid[index] = nextColor;
            return newGrid;
        });
    };

    /** Submit + compute accuracy */
    const handleSubmit = async () => {
        const accuracy = await submitAnswerGrid(
            room.id,
            selectedGrid.id,
            user.uid,
            answers
        );
        setScore(accuracy);
        setLocalStage("result");
    };

    /** Result-phase continue */
    const handleResultContinue = async () => {
        if (hasClickedContinue) return;
        await handleContinueClick(room.id, user, stage);
        setHasClickedContinue(true);
    };

    /** Listen if everyone has pressed Continue */
    useEffect(() => {
        if (!room?.id || !players?.length) return;
        const unsub = listenForAllReady(room.id, players.length, stage, setStage);
        return () => unsub && unsub();
    }, [room.id, players, stage, setStage]);

    // ------------------------- UI -------------------------
    return (
        <div className="w-full min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
            <h1 className="text-3xl sm:text-5xl font-bold mb-4 tracking-wide">
                Color&nbsp;Coding
            </h1>

            {/* WAITING */}
            {localStage === "waiting" && (
                <>
                    <p className="text-zinc-400 mb-6">
                        Press Continue to get your grid and start the challenge!
                    </p>
                    <button
                        onClick={handleContinue}
                        className="px-8 py-2 border border-white rounded-lg hover:bg-white hover:text-black transition-all duration-300"
                    >
                        Continue
                    </button>
                </>
            )}

            {/* SHOWING GRID */}
            {localStage === "showing" && selectedGrid && (
                <>
                    <TimerDisplay seconds={timer} />
                    <ColorGridDisplay grid={selectedGrid?.grid || []} />
                    <p className="text-zinc-400 text-sm mt-4">
                        Memorize this color pattern carefully.
                    </p>
                </>
            )}

            {/* ANSWERING */}
            {localStage === "answering" && (
                <>
                    <p className="text-zinc-300 mb-4">
                        Recreate the color pattern you just saw 👇
                    </p>
                    <ColorGridAnswer
                        answers={answers}
                        handleBoxClick={handleBoxClick}
                        handleSubmit={handleSubmit}
                    />
                </>
            )}

            {/* RESULT */}
            {localStage === "result" && (
                <div className="flex flex-col items-center justify-center">
                    <h3 className="text-2xl text-green-400 font-semibold mb-2">
                        You scored {score}% accuracy!
                    </h3>
                    <p className="text-zinc-400 mb-6 text-sm">
                        Original grid for reference:
                    </p>
                    <ColorGridDisplay grid={selectedGrid?.grid || []} />

                    {/* Continue Button */}
                    <button
                        onClick={handleResultContinue}
                        disabled={hasClickedContinue}
                        className="mt-6 px-8 py-2 border border-white rounded-lg hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-40"
                    >
                        {hasClickedContinue ? "Waiting for others..." : "Continue"}
                    </button>
                </div>
            )}
        </div>
    );
}